import 'select2';

const t = (key, fallback = '') => (window.BL_I18N && BL_I18N[key]) || fallback || key;
const CURRENT_LOCALE = (window.BL_I18N && BL_I18N.locale) || document.documentElement.lang || 'en-US';

const IS_DE = String(CURRENT_LOCALE).toLowerCase().startsWith('de');

// Selectors
const SELECTORS = {
    FORM: '#booking-form',
};

function readJsonScript(id) {
    const el = document.getElementById(id);
    if (!el) return null;
    try { return JSON.parse(el.textContent || '{}'); }
    catch { return null; }
}

class LongTermBookingApi {

    constructor() {
        this.proxyUrl = '/wp-content/themes/basellodging-theme/api-proxi.php';

        this.$app      = $('#booking-app');
        this.$loader   = $('#booking-loader');
        this.$content  = $('#booking-content');
        this.$error    = $('#booking-error');
        this.$form     = $(SELECTORS.FORM);
        this.$extras   = $('#js-extras-list');
        this.$card     = $('#js-apartment-card');
        this.$priceBox = $('#js-price-summary');
        this.$submit   = $('#js-book-submit');

        this.lang = (
            (window.ICL_LANGUAGE_CODE) ||
            (window.BL_AJAX && BL_AJAX.lang) ||
            (window.BL_I18N && BL_I18N.locale) ||
            document.documentElement.lang || 'en'
        ).toString().toLowerCase().split(/[-_]/)[0];

        this.selectedExtras = new Map();   // id -> { id, name, price, frequency }

        this.data = readJsonScript('bl-booking-data') || {};
        this.currency = this.data.currency || 'CHF';
        this.locale   = this.data.locale   || 'de-CH';

        // from payload we expect: apartmentId, propertyIdentifier, dateFrom, dateTo, guests
        this.payload = this.data.payload || {};
        this.apartmentId        = String(this.payload.apartmentId || this.payload.unitId || '');
        this.dateFrom = this.payload.dateFrom || this.payload.checkIn  || '';
        this.dateTo   = this.payload.dateTo   || this.payload.checkOut || '';
        this.guests   = Number(this.payload.guests || 1);

        this.preselectedExtraIds = new Set(
            Array.isArray(this.payload.extras) ? this.payload.extras.map(String) : []
        );

        this._hiddenExtrasSlugsSet = new Set(
            Array.isArray(this.payload.hiddenExtras)
                ? this.payload.hiddenExtras.map(s => this.toFeatureId(s))
                : []
        );

        // data from API
        this.availability = null;   // full response (unit, rateAvailability, etc.)
        this.apartment    = null;   // info for the card (title, location, photos, ... )
        this.apartmentTitle    = null;
        this.extras       = [];     // array of extra services

        // initialize select2 + validation right away (form is hidden but ready)
        this.initSelects(this.$form);
        this.validateForm();

        this.stripe     = null;
        this.elements   = null;
        this.cardNumber = null;
        this.cardExpiry = null;
        this.cardCvc    = null;

        this.lastTotal  = 0;

        // Extras assets (array of { slug, icon, logo })
        this.extrasAssets = (window.BL_AJAX && (
            BL_AJAX.extras_icons_assets ||
            BL_AJAX.extras_icons_items  ||
            BL_AJAX.extras_assets
        )) || [];

        // Backward-compat: old map (slug -> iconUrl) if exists
        this.extrasIconMap = (window.BL_AJAX && BL_AJAX.extras_icons_map) ? BL_AJAX.extras_icons_map : {};

        // Normalize assets to array
        if (!Array.isArray(this.extrasAssets)) this.extrasAssets = [];

        // Fast lookups
        this._extrasIconBySlug = Object.create(null);
        this._extrasLogoBySlug = Object.create(null);
        this._reindexExtrasAssets();

        this._missingExtrasSlugs = new Set();
        this._extrasIconsSyncing = false;

        this.checkoutToken = this.data?.token || window.BL_CHECKOUT_TOKEN || '';
    }

    _buildExtrasOrderIndex() {
        // Order comes from BL_AJAX.extras_assets array (slug order)
        const order = Object.create(null);

        const arr = Array.isArray(this.extrasAssets) ? this.extrasAssets : [];
        let pos = 0;

        for (let i = 0; i < arr.length; i++) {
            const slug = String(arr[i]?.slug || '').trim();
            if (!slug) continue;
            if (slug === 'default') continue;

            // keep first occurrence only
            if (order[slug] == null) {
                order[slug] = pos++;
            }
        }

        return order;
    }

    _getExtrasSortedByAssetsOrder(extras) {
        const list = Array.isArray(extras) ? extras : [];
        if (!list.length) return [];

        const order = this._buildExtrasOrderIndex();

        // stable sort: preserve API order inside same bucket
        return list
            .map((svc, originalIndex) => {
                const slug = this.getExtraSlugFromService(svc);
                const ord = (slug && order[slug] != null) ? order[slug] : Number.MAX_SAFE_INTEGER;
                return { svc, ord, originalIndex };
            })
            .sort((a, b) => (a.ord - b.ord) || (a.originalIndex - b.originalIndex))
            .map(x => x.svc);
    }

    _reindexExtrasAssets() {
        // English comments as requested
        this._extrasIconBySlug = Object.create(null);
        this._extrasLogoBySlug = Object.create(null);

        const arr = Array.isArray(this.extrasAssets) ? this.extrasAssets : [];
        for (let i = 0; i < arr.length; i++) {
            const it = arr[i] || {};
            const slug = String(it.slug || '').trim();
            if (!slug) continue;

            const icon = String(it.icon || '').trim();
            const logo = String(it.logo || '').trim();

            if (icon) this._extrasIconBySlug[slug] = icon;
            if (logo) this._extrasLogoBySlug[slug] = logo;
        }
    }

    _mergeExtrasAssets(incoming) {
        // Accept array of items OR object map, be tolerant
        let items = [];

        if (Array.isArray(incoming)) {
            items = incoming;
        } else if (incoming && typeof incoming === 'object') {
            items = Object.keys(incoming).map((slug) => {
                const v = incoming[slug];
                if (v && typeof v === 'object') return { slug, icon: v.icon || '', logo: v.logo || '' };
                return { slug, icon: v || '', logo: '' };
            });
        }

        if (!items.length) return;

        // Merge into this.extrasAssets by slug
        const bySlug = Object.create(null);
        (this.extrasAssets || []).forEach((it) => {
            const s = String(it?.slug || '').trim();
            if (!s) return;
            bySlug[s] = { ...it };
        });

        items.forEach((it) => {
            const s = String(it?.slug || '').trim();
            if (!s) return;

            const prev = bySlug[s] || { slug: s };
            bySlug[s] = {
                slug: s,
                icon: (it.icon != null && String(it.icon).trim() !== '') ? it.icon : (prev.icon || ''),
                logo: (it.logo != null && String(it.logo).trim() !== '') ? it.logo : (prev.logo || ''),
            };
        });

        this.extrasAssets = Object.values(bySlug);
        this._reindexExtrasAssets();
    }

    /* ---------- utils ---------- */
    currencyFormat(v) {
        const n = Number(v); if (!Number.isFinite(n)) return '';
        try {
            return new Intl.NumberFormat(this.locale, { style: 'currency', currency: this.currency, minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(n);
        } catch { return `${this.currency} ${n.toFixed(0)}`; }
    }
    currencyFormatTwoDecimals(v) {
        const n = Number(v); if (!Number.isFinite(n)) return '';
        try {
            return new Intl.NumberFormat(this.locale, { style: 'currency', currency: this.currency, minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
        } catch { return `${this.currency} ${n.toFixed(0)}`; }
    }

    escapeHtml(s){return String(s).replace(/[&<>"']/g,m=>({ "&":"&amp;","<":"&lt;",">":"&gt;",'"':"&quot;","'":"&#39;"}[m]));}

    nights(df,dt){
        if(!df||!dt)return 0;
        const a=new Date(df+'T00:00:00Z'), b=new Date(dt+'T00:00:00Z');
        return Math.max(0, Math.round((b-a)/86400000));
    }

    async loadPhonesAjax() {
        const [list, preferred] = await Promise.all([
            $.getJSON('/wp-json/basel/v1/phones'),
            $.getJSON('/wp-json/basel/v1/preferred'),
        ]);
        this._phonesList = Array.isArray(list) ? list : [];
        this._preferredCodes = Array.isArray(preferred) ? preferred : [];
    }

    toFeatureId(slug = '') {
        // PHP analog of bl_normalize_extra_slug()
        slug = String(slug || '').trim().toLowerCase();

        // spaces -> underscores
        slug = slug.replace(/\s+/g, '_');

        // everything except a-z, 0-9, underscore and dash -> underscore
        slug = slug.replace(/[^a-z0-9_-]+/g, '_');

        // collapse multiple underscores
        slug = slug.replace(/_+/g, '_');

        // trim underscores/dashes from start and end
        slug = slug.replace(/^[_-]+|[_-]+$/g, '');

        return slug;
    }

    getExtraSlugFromService(svc) {
        // Prefer stable API-provided slug if exists
        if (svc && svc.slug) return this.toFeatureId(svc.slug);

        // Use original (non-localized) name to stay consistent across languages
        const name = String((svc && (svc.name || svc.webName || svc.title)) || '').trim();
        return this.toFeatureId(name);
    }

    getExtraIconUrlBySlug(slug) {
        const s = String(slug || '').trim();

        // Prefer assets first
        if (s && this._extrasIconBySlug && this._extrasIconBySlug[s]) return this._extrasIconBySlug[s];

        // Backward-compat: old map
        const map = this.extrasIconMap || {};
        if (s && Object.prototype.hasOwnProperty.call(map, s) && map[s]) return map[s];

        // fallback to default
        if (this._extrasIconBySlug && this._extrasIconBySlug['default']) return this._extrasIconBySlug['default'];
        if (Object.prototype.hasOwnProperty.call(map, 'default') && map['default']) return map['default'];

        return '';
    }

    getExtraLogoUrlBySlug(slug) {
        const s = String(slug || '').trim();
        if (s && this._extrasLogoBySlug && this._extrasLogoBySlug[s]) return this._extrasLogoBySlug[s];

        // optional fallback
        if (this._extrasLogoBySlug && this._extrasLogoBySlug['default']) return this._extrasLogoBySlug['default'];
        return '';
    }

    queueMissingExtraSlug(slug) {
        const s = String(slug || '').trim();
        if (!s) return;

        // If we already have icon or logo for this slug — nothing to sync
        const hasIcon = !!(this._extrasIconBySlug && this._extrasIconBySlug[s]);
        const hasLogo = !!(this._extrasLogoBySlug && this._extrasLogoBySlug[s]);

        // Also treat legacy icon map as "has icon"
        const legacyHasIcon = !!(this.extrasIconMap && this.extrasIconMap[s]);

        if (hasIcon || hasLogo || legacyHasIcon) return;

        this._missingExtrasSlugs.add(s);
    }

    flushMissingExtrasIcons() {
        if (this._extrasIconsSyncing) return;

        const slugs = Array.from(this._missingExtrasSlugs);
        if (!slugs.length) return;

        const ajaxUrl = (window.BL_AJAX && BL_AJAX.url) || '/wp-admin/admin-ajax.php';
        const nonce   = (window.BL_AJAX && (BL_AJAX.extras_icons_nonce || BL_AJAX.nonce)) || '';

        this._missingExtrasSlugs.clear();
        this._extrasIconsSyncing = true;

        $.ajax({
            url: ajaxUrl,
            method: 'POST',
            dataType: 'json',
            data: {
                action: 'bl_sync_extras_icons',
                _ajax_nonce: nonce,
                slugs: slugs
            }
        })
            .done((res) => {
                // Expect: res.data.items = [{slug,icon,logo}, ...]
                const payload  = res?.data || {};
                const incoming = payload.items || payload.assets || payload.map || payload;

                this._mergeExtrasAssets(incoming);

                // Optional: keep legacy map in sync (icon only), so old code won't break
                if (Array.isArray(this.extrasAssets)) {
                    this.extrasIconMap = this.extrasIconMap || {};
                    this.extrasAssets.forEach((it) => {
                        const s = String(it?.slug || '').trim();
                        if (!s) return;
                        if (it.icon) this.extrasIconMap[s] = it.icon;
                    });
                }
            })
            .always(() => {
                this._extrasIconsSyncing = false;
            });
    }

    populateDialCodesFromData() {
        const list = this._phonesList || [];
        const prefSet = new Set((this._preferredCodes || []).map(c => String(c).toUpperCase()));
        const byCode = new Map(list.map(x => [String(x.code||'').toUpperCase(), x]));

        const first = [];
        (this._preferredCodes || []).forEach(code => {
            const up = String(code).toUpperCase();
            if (byCode.has(up)) first.push(byCode.get(up));
        });
        const rest = list.filter(x => !prefSet.has(String(x.code||'').toUpperCase()));
        const finalList = first.concat(rest);

        const html = finalList.map(x => {
            const dial = String(x.dial_code||'').trim();
            const iso  = String(x.code||'').toUpperCase();
            return `<option value="${this.escapeHtml(dial)}">${this.escapeHtml(`${dial} ${iso}`)}</option>`;
        }).join('');

        this.$form.find('select[name="phone-code"]').html(html);

        // re-init select2 для свіжого DOM
        this.initSelects(this.$form);
    }

    computeExtraCost(ex){               // price of an extra service (0 if missing)
        const p = Number(ex?.price || 0);
        return Number.isFinite(p) ? p : 0;
    }

    formatFrequency(freq){
        if(!freq) return 'One-time fee';
        const f = String(freq).toLowerCase();
        if (f==='one_time' || f==='one-time') return 'One-time fee';
        if (f==='per_night') return 'Per night';
        if (f==='per_stay')  return 'Per stay';
        return f;
    }

    getExtrasTotal(){
        let t = 0;
        this.selectedExtras.forEach(ex => t += this.computeExtraCost(ex));
        return t;
    }

    showGlobalError(msg) {
        const $box = $('#js-form-global-error');
        if ($box.length) {
            $box.text(msg || '').toggleClass('hidden', !msg);
        } else {
            alert(msg || t('something_went_wrong', 'Something went wrong.'));
        }
    }


    /* ---------- UI: loader / content / error ---------- */
    showLoader(){ this.$loader.removeClass('hidden'); this.$content.addClass('hidden'); this.$error.addClass('hidden'); }
    showContent(){ this.$loader.addClass('hidden'); this.$error.addClass('hidden'); this.$content.removeClass('hidden'); }
    showError(msg){
        this.$loader.addClass('hidden');
        this.$error.text(msg || t('apartment_is_not_available', 'The apartment is not available on these dates.')).removeClass('hidden');
        this.$content.addClass('hidden');
    }

    /* ---------- AJAX ---------- */

    // 1) Availability check (Promise)
    checkAvailability() {
        const ajaxUrl = (window.BL_AJAX && BL_AJAX.url) || '/wp-admin/admin-ajax.php';
        const nonce   = (window.BL_AJAX && BL_AJAX.nonce) || '';

        const unitTypeType = (this.data?.payload?.unitTypeType || '').toString();
        const maxGuests = (this.data?.payload?.maxGuests || '').toString();

        return $.ajax({
            url: ajaxUrl,
            method: 'POST',
            dataType: 'json',
            data: {
                action: 'bl_check_availability',
                _ajax_nonce: nonce,
                lang: this.lang,
                dateFrom: this.dateFrom,
                dateTo:   this.dateTo,
                guests:   String(this.guests),
                apartmentId: this.apartmentId,
                maxGuests, unitTypeType
            }
        }).then((res) => {
            if (!res?.success || (res?.data?.available === false)) {
                const msg = res?.data?.message || t('apartment_is_not_available', 'The apartment is not available on these dates.');
                return Promise.reject(new Error(msg));
            }
            // store payload in the class
            this.availability = res.data || {};
            // put unit in a convenient place
            this.apartment = this.availability.unit || this.availability;
            this.apartmentTitle = this.availability.unitTitle || 'Apartment';
            return this.availability;
        });
    }

    // 2) Detailed info for card/extras (Promise)
    fetchApartmentInformation() {
        if (!this.apartmentId) return Promise.resolve({});
        return $.ajax({
            url: `${this.proxyUrl}?type=getApartmentInformation`,
            method: 'POST',
            contentType: 'application/json',
            dataType: 'json',
            data: JSON.stringify({ apartment_id: this.apartmentId })
        }).then((resp) => {
            const data = (resp && typeof resp === 'object' && 'data' in resp) ? resp.data : resp;
            this.apartmentInfo = data || {};
            this.extras = Array.isArray(data?.extras) ? data.extras : [];
            return data;
        });
    }

    /* ---------- RENDER ---------- */

    renderApartmentCard() {
        const title    = this.apartmentTitle;
        const location = this.apartmentInfo?.location || this.apartment?.location || '';
        const guests   = Math.max(1, Number(this.guests) || 1);
        const br       = (this.apartmentInfo?.specifications?.numberOfBedrooms ?? 0);
        const brLabel  = (Number(br) === 0 ? 'Studio' : `${br} BR`);
        const bath     = (this.apartmentInfo?.bathrooms ?? this.apartment?.bathrooms ?? 1);
        const areaMin  = this.apartmentInfo?.specifications?.area?.min || '';
        const areaMax  = this.apartmentInfo?.specifications?.area?.max || '';
        const mainImg  = this.apartmentInfo?.mainImage?.url || this.apartmentInfo?.images?.[0] || '';
        const area     = areaMin && areaMax && areaMin !== areaMax ? `${areaMin}–${areaMax}m²` : (areaMin || areaMax ? `${areaMin || areaMax}m²` : '');

        const base = '/wp-content/themes/basellodging-theme/imgs';
        const placeholder = base ? base + "/placeholder.svg" : "/placeholder.svg";

        const n = this.nights(this.dateFrom, this.dateTo);

        this.$card.html(`
      <div class="rounded-lg bg-card text-card-foreground shadow-lg border border-gray-200 overflow-hidden">
        <div class="relative">
          <img src="${mainImg? mainImg : placeholder}"
               alt="${this.escapeHtml(title)}" class="w-full h-48 object-cover">
        </div>
        <div class="p-4">
          ${location ? `
            <div class="mb-2">
              <div class="flex items-center text-sm text-gray-600">
                <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 mr-1" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0"></path><circle cx="12" cy="10" r="3"></circle></svg>
                ${this.escapeHtml(location)}
              </div>
            </div>
          ` : ``}
          <h3 class="font-semibold text-basel-gray mb-3">${this.escapeHtml(title)}</h3>

          <div class="grid grid-cols-2 gap-3 p-3 bg-gray-50 rounded-lg">
            <div class="flex items-center gap-1 text-sm text-basel-gray">
              <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 text-basel-blue" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M22 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>
              <span>${guests} ${guests===1?t('guest','Guest').toLowerCase():t('guests','Guests').toLowerCase()}</span>
            </div>
            <div class="flex items-center gap-1 text-sm text-basel-gray">
              <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 text-basel-blue" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path d="M2 4v16"></path><path d="M2 8h18a2 2 0 0 1 2 2v10"></path><path d="M2 17h20"></path><path d="M6 8v9"></path></svg>
              <span>${this.escapeHtml(brLabel)}</span>
            </div>
            <div class="flex items-center gap-1 text-sm text-basel-gray">
              <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 text-basel-blue" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path d="M10 4 8 6"></path><path d="M17 19v2"></path><path d="M2 12h20"></path><path d="M7 19v2"></path><path d="M9 5 7.621 3.621A2.121 2.121 0 0 0 4 5v12a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-5"></path></svg>
              <span>${bath} ${bath===1?t('bath','bath').toLowerCase():t('baths','baths').toLowerCase()}</span>
            </div>
            ${area ? `
            <div class="flex items-center gap-1 text-sm text-basel-gray">
              <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 text-basel-blue" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><rect width="18" height="18" x="3" y="3" rx="2"></rect></svg>
              <span>${this.escapeHtml(area)}</span>
            </div>` : ``}
          </div>
        </div>
      </div>
    `);
    }

    pruneHiddenSelectedExtras() {
        const hidden = this._hiddenExtrasSlugsSet;
        if (!hidden || !hidden.size) return;

        // Build the same id index as in hydrateSelectedExtrasFromPayload()
        const byId = new Map((this.extras || []).map(s => [
            String(s.externalId ?? s.id ?? s.extraId ?? ''),
            s
        ]));

        const idsToDelete = [];

        this.selectedExtras.forEach((_ex, id) => {
            const svc = byId.get(String(id));
            if (!svc) return;

            const slug = this.getExtraSlugFromService(svc);
            if (slug && hidden.has(slug)) idsToDelete.push(String(id));
        });

        idsToDelete.forEach(id => this.selectedExtras.delete(id));
    }

    renderExtras() {
        if (!this.extras.length) {
            this.$extras.html('<div class="text-sm text-gray-500">' + t('no_extra_services', 'No extra services available.') + '</div>');
            return;
        }

        const freqLabel = (f) => {
            const v = String(f || '').toLowerCase();
            if (v === 'monthly') return t('freq_monthly','monthly');
            if (v === 'weekly')  return t('freq_weekly','weekly');
            if (v === 'daily')   return t('freq_daily','daily');
            return t('freq_one_off','one-off');
        };

        const extrasSorted = this._getExtrasSortedByAssetsOrder(this.extras);

        const rows = extrasSorted.map((s) => {
            if (s.webCategoryName !== 'long') return '';

            const id = String(s.id ?? '');
            const name = IS_DE ? (s.descriptionLang2 || "") : (s.name || "");
            const desc = (() => {
                const src = String(s.webDescription || '').trim();
                if (!src) return '';
                const parts = src.split('/').map(p => p.trim()).filter(Boolean);
                if (!parts.length) return '';
                return IS_DE ? parts[parts.length - 1] : parts[0];
            })();

            const freq = s.billing_frequency ? String(s.billing_frequency).trim() : '';
            const freqLower = freq.toLowerCase();

            const price = Number(s.price || 0);
            const priceStr = this.currencyFormatTwoDecimals(price);

            const isOn = this.selectedExtras.has(id);

            const slug = this.getExtraSlugFromService(s);

            if (this._hiddenExtrasSlugsSet && this._hiddenExtrasSlugsSet.has(slug)) return '';

            // ensure default exists
            this.queueMissingExtraSlug('default');

            // if slug is new — queue it
            this.queueMissingExtraSlug(slug);

            const iconUrl = this.getExtraIconUrlBySlug(slug);
            const logoUrl = this.getExtraLogoUrlBySlug(slug);

            const iconHtml = iconUrl
                ? `<img class="w-8 h-8 object-contain" src="${this.escapeHtml(iconUrl)}" alt="" loading="lazy" />`
                : '';

            const partnerHtml = logoUrl ? `
            <div class="sm:flex shrink-0 items-center justify-center logo-box">
              <div class="flex flex-col items-center sm:items-start gap-2">
                <img class="w-24 h-24 object-contain" src="${this.escapeHtml(logoUrl)}" alt="" loading="lazy" />
              </div>
            </div>
        ` : '';

            return `
            <div class="bg-white p-4 border border-blue-100 rounded-lg transition-colors ${isOn ? 'border-green-500 bg-green-50' : ''}"
                 data-extra data-id="${this.escapeHtml(id)}" data-name="${this.escapeHtml(name)}"
                 data-frequency="${this.escapeHtml(freqLower)}">
                <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-0 extras-box">

                    <!-- Col 1: Icon -->
                    <div class="flex items-center justify-center gap-3 shrink-0 icon-box">
                        ${iconHtml ? `<span class="inline-flex items-center justify-center">${iconHtml}</span>` : ''}
                    </div>

                    <!-- Col 2: Info -->
                    <div class="info-box">
                        <div class="flex flex-wrap items-center gap-2">
                            <h3 class="font-medium text-basel-gray text-sm sm:text-base break-words">
                                ${this.escapeHtml(name)}
                            </h3>
                            ${freqLower ? `
                                <span class="text-xs bg-gray-100 text-gray-600 border border-gray-300 rounded-full px-2 py-0.5">
                                  ${this.escapeHtml(freqLabel(freqLower))}
                                </span>
                            ` : ''}
                        </div>

                        ${desc ? `<p class="mt-1 text-xs sm:text-sm text-gray-600">${this.escapeHtml(desc)}</p>` : ''}
                    </div>

                    <!-- Price -->
                    <div class="flex items-baseline gap-2 price-box">
                        ${price ? `
                            <div class="font-bold text-basel-gray text-sm sm:text-base">
                              ${this.escapeHtml(priceStr)}
                            </div>
                        ` : ''}
                    </div>

                    <!-- Col 3: Partner Logo -->
                    ${partnerHtml}

                    <!-- Col 4: CTA (keep booking button class) -->
                    <div class="flex sm:ml-auto shrink-0 justify-end cta-box">
                        <button type="button"
                                class="inline-flex items-center justify-center gap-2 text-sm h-9 rounded-md px-3 js-extra-book
                                       ${isOn ? 'bg-green-600 hover:bg-green-700 text-white' : 'border bg-background border-gray-300 text-basel-gray hover:bg-gray-50'}"
                                data-id="${this.escapeHtml(id)}"
                                data-name="${this.escapeHtml(name)}"
                                data-price="${price}"
                                data-frequency="${this.escapeHtml(freqLower)}">
                            ${isOn ? t('added', 'Added') : t('add', 'Add')}
                        </button>
                    </div>

                </div>
            </div>
        `;
        }).join('');

        this.$extras.html(rows);

        // important: try to sync missing icons/logos
        this.flushMissingExtrasIcons();
    }

    hydrateSelectedExtrasFromPayload() {
        if (!this.preselectedExtraIds || !this.preselectedExtraIds.size) return;

        const byId = new Map((this.extras || []).map(s => [
            String(s.externalId ?? s.id ?? s.extraId ?? ''),
            s
        ]));

        this.selectedExtras.clear();

        this.preselectedExtraIds.forEach((rawId) => {
            const id = String(rawId);
            if (!byId.has(id)) return;

            const s = byId.get(id);
            this.selectedExtras.set(id, {
                id,
                name: String(s.extraName ?? s.name ?? ''),
                price: Number(s.netPrice ?? s.price ?? 0),
                frequency: String(s.billing_frequency ?? s.frequency ?? '').toLowerCase()
            });
        });
    }

    formatPrettyDate(iso, locale = 'en-US') {
        if (!iso) return '';
        const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso));
        if (!m) return iso;
        const [ , y, mo, d ] = m;
        // Створюємо UTC-дату, щоб уникнути зсувів таймзони
        const dt = new Date(Date.UTC(+y, +mo - 1, +d));
        try {
            return new Intl.DateTimeFormat(locale, {
                month: 'short', day: 'numeric', year: 'numeric'
            }).format(dt); // -> "Jun 25, 2025"
        } catch {
            const mon = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
            return `${mon[dt.getUTCMonth()]} ${+d}, ${y}`;
        }
    }


    // returns total (number)
    renderPriceBox() {
        const ra = this.apartment?.rateAvailabilities?.[0]
            || this.apartment?.rateAvailabilities
            || null;

        if(!ra) return 0;

        const priceMonthly = Number(this.apartmentInfo?.pricing[0]?.amount ?? ra?.priceFrom?.gross) || 0;

        // if backend provides totals.gross — use it as total without extra services
        const totalApi   = Number(ra?.totals?.gross || 0);

        const extrasTotal = this.getExtrasTotal();
        const extrasLine = `
          <div class="flex justify-between"><span>${t('extra_services','Extra services')}</span><span>${this.currencyFormat(extrasTotal)}</span></div>
        `;

        // client-selected extra services are not added here — basic render
        const extrasSum  = 0;

        let total = (priceMonthly > 0 ? priceMonthly : totalApi) + extrasTotal;
        const long_term_rental_title   = (window.BL_AJAX && BL_AJAX.long_term_rental_title) || '';
        const long_term_rental_text   = (window.BL_AJAX && BL_AJAX.long_term_rental_text) || '';

        const box = `
         <div class="rounded-lg bg-card  border border-gray-200 overflow-hidden shadow-lg">
             <div class="flex flex-col space-y-1.5 p-6">
                 <h3 class="text-2xl font-semibold leading-none tracking-tight flex items-center justify-between">
                     <div>
                         <span class="text-2xl font-bold">${this.currencyFormat(priceMonthly)}</span>
                     </div>
                     <span class="text-sm text-gray-600">${t('per_month','per month')}</span>
                 </h3>
             </div>
             <div class="p-6 pt-0 space-y-4">
                 <div class="space-y-2">
                     <div class="flex justify-between items-center py-2 border-b border-gray-100">
                         <div class="flex items-center text-sm text-basel-gray">
                             <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                                  stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                                  class="lucide lucide-calendar w-4 h-4 mr-2">
                                 <path d="M8 2v4"></path>
                                 <path d="M16 2v4"></path>
                                 <rect width="18" height="18" x="3" y="4" rx="2"></rect>
                                 <path d="M3 10h18"></path>
                             </svg>
                             ${t('arrival','Arrival Date')}
                         </div>
                         <span class="text-sm font-medium">${this.escapeHtml(this.formatPrettyDate(this.dateFrom, CURRENT_LOCALE))}</span>
                     </div>
                     <div class="flex justify-between items-center py-2 border-b border-gray-100">
                         <div class="flex items-center text-sm text-basel-gray">
                             <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                                  stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                                  class="lucide lucide-calendar w-4 h-4 mr-2">
                                 <path d="M8 2v4"></path>
                                 <path d="M16 2v4"></path>
                                 <rect width="18" height="18" x="3" y="4" rx="2"></rect>
                                 <path d="M3 10h18"></path>
                             </svg>
                             ${t('departure','Departure Date')}
                         </div>
                         <span class="text-sm font-medium">${this.escapeHtml(this.formatPrettyDate(this.dateTo, CURRENT_LOCALE))}</span>
                     </div>
                 </div>
                 <div class="space-y-2 pt-4">
                     <div class="flex justify-between"><span>${t('monthly_rent','Monthly rent')}</span><span>${this.currencyFormat(priceMonthly)}</span></div>
                     <div data-orientation="horizontal" role="none" class="shrink-0 bg-border h-[1px] w-full"></div>
                     ${extrasTotal > 0? extrasLine : ''}
                     <div class="flex justify-between font-bold text-lg"><span>${t('total_per_month','Total per month')}</span><span>${this.currencyFormat(priceMonthly + extrasTotal)}</span></div>
                 </div>
                 <div class="bg-white border border-gray-200 rounded-lg p-4 mt-4">
                     <h4 class="font-semibold text-basel-gray mb-3">
                         ${long_term_rental_title}
                     </h4>
                     <div class="text-sm text-basel-gray space-y-2">
                         ${long_term_rental_text}
                     </div>
                 </div>
             </div>
         </div>
        `;

        this.$priceBox.html(box);

        this.lastTotal = Number(total) || 0;

        return total;
    }

    // AJAX: fetch HTML form and inject into the placeholder
    loadFormHtml() {
        const ajaxUrl = (window.BL_AJAX && BL_AJAX.url) || '/wp-admin/admin-ajax.php';
        const nonce   = (window.BL_AJAX && BL_AJAX.nonce) || '';

        return $.ajax({
            url: ajaxUrl,
            method: 'POST',
            dataType: 'json',
            data: { action: 'bl_get_long_booking_form', _ajax_nonce: nonce, lang: this.lang }
        }).then((res) => {
            if (!res?.success || !res?.data?.html) {
                const msg = t('could_not_load_the_booking_form', 'Could not load the booking form.');
                return Promise.reject(new Error(msg));
            }

            const $wrap = $('[data-role="form"]');      // placeholder for the form
            $wrap.html(res.data.html);                  // insert the form

            // after insertion re-initialize element references
            this.$form   = $(SELECTORS.FORM);
            this.$extras = $('#js-extras-list');
            this.$submit = $('#js-book-submit');

            // initialize select2/validation on the dynamically loaded form
            this.initSelects(this.$form);
            this.validateForm();
        });
    }

    /* ---------- form/init ---------- */

    initSelects($scope) {
        const initOne = ($el, placeholder) => {
            if ($el.hasClass('select2-hidden-accessible')) $el.select2('destroy');
            $el.select2({
                minimumResultsForSearch: 0,
                width: '100%',
                dropdownParent: $el.closest('.select-box'),
                dropdownAutoWidth: true,
                placeholder, theme: 'basel'
            });
        };
        [
            { el: $scope.find('select[name="phone-code"]'),            placeholder: '' },
        ].forEach(({el, placeholder}) => el.length && initOne(el, placeholder));
    }

    parseAjaxError(err) {
        try {
            // jQuery xhr
            if (err && err.responseJSON) {
                const rj = err.responseJSON;
                // WP: { success:false, data:{message:"..."} }
                if (rj.data && rj.data.message) return rj.data.message;
                if (rj.message) return rj.message;
            }
            if (err && err.responseText) {
                // інколи приходить plain text або JSON строкою
                try {
                    const j = JSON.parse(err.responseText);
                    if (j?.data?.message) return j.data.message;
                    if (j?.message) return j.message;
                } catch { /* ignore */ }
                return String(err.responseText).trim();
            }
            // звичайний Error
            if (err && err.message) return err.message;
        } catch {}
        return t('unexpected_error', 'Unexpected error. Please try again.');
    }

    setupFormValidation(form) {
        let self = this;

        $.extend($.validator.messages, {
            required:  t('val_required',  'This field is required.'),
            email:     t('val_email',     'Please enter a valid email address.'),
            number:    t('val_number',    'Please enter a valid number.'),
            digits:    t('val_digits',    'Please enter only digits.'),
            minlength: $.validator.format(t('val_minlength', 'Please enter at least {0} characters.')),
            maxlength: $.validator.format(t('val_maxlength', 'Please enter no more than {0} characters.')),
            equalTo:   t('val_equalTo',   'Please enter the same value again.')
        });

        $.validator.addMethod('blEmail', function (value, element) {
            if (this.optional(element)) return true;

            const v = String(value || '').trim();
            if (!v || v.length > 254) return false;

            // local-part ≤ 64 (як рекомендація)
            const at = v.indexOf('@');
            if (at <= 0 || at > 64) return false;

            // Базовий RFC5322-lite + TLD 2+
            const re = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
            if (!re.test(v)) return false;

            // Забороняємо подвійні крапки у домені
            const domain = v.slice(at + 1);
            if (domain.includes('..')) return false;

            return true;
        }, t('val_email', 'Please enter a valid email address.'));

        $.validator.addMethod('blPhoneLocal', function (value, element, params) {
            if (this.optional(element)) return true;
            const digits = String(value || '').replace(/\D/g, '');
            const min = (params && params.min) || 6;
            const max = (params && params.max) || 12;
            return digits.length >= min && digits.length <= max;
        }, t('val_phone', 'Please enter a valid phone number.'));


        $(SELECTORS.FORM).validate({
            focusInvalid: false,

            invalidHandler: function (_event, validator) {
                const first = validator.errorList?.[0]?.element;
                if (first) {
                    const $first = $(first);
                    const $wrap = $first.parent();
                    self.scrollToElem($wrap);
                    setTimeout(() => $first.trigger('focus'), 0);
                }
                $(this).find('button[type="submit"]').prop('disabled', false).removeClass('loading');
            },

            submitHandler: async (form, event) => {
                event.preventDefault();
                this.submitAttempted = true;

                this.showGlobalError('');

                const $form = $(form);
                const $btn  = $form.find('button[type="submit"]');
                $btn.prop('disabled', true).addClass('loading');

                try {
                    const plainValid = this.validateInputs();
                    if (!plainValid) return;

                    await this.onSubmitLongFlow();
                } catch (err) {
                    this.showGlobalError(this.parseAjaxError(err));
                } finally {
                    $btn.prop('disabled', false).removeClass('loading');
                }
            },

            rules: {
                phoneNumber: {
                    blPhoneLocal: {
                        param: { min: 6, max: 12 }
                    }
                },
                email: {
                    blEmail: true
                },
            },

            errorPlacement: function (error, element) {
                if(element.is('[type="checkbox"]')) return;
                error.appendTo(element.parent());
            },

            highlight: function (element) {
                $(element).parent().addClass('error');
            },

            unhighlight: function (element) {
                $(element).parent().removeClass('error');
            }
        });
    }

    async onSubmitLongFlow() {
        const ajaxUrl = (window.BL_AJAX && BL_AJAX.url) || '/wp-admin/admin-ajax.php';
        const nonce   = (window.BL_AJAX && BL_AJAX.nonce) || '';

        const $country = this.$form.find('select[name="country"] option:selected');
        const countryCode = String($country.val()||'').trim();
        const countryName = String($country.text()||'').trim();

        const customer = {
            firstName: String(this.$form.find('input[name="firstName"]').val() || '').trim(),
            lastName:  String(this.$form.find('input[name="lastName"]').val()  || '').trim(),
            email:     String(this.$form.find('input[name="email"]').val()      || '').trim(),
            phone:     String(this.$form.find('select[name="phone-code"]').val() || '').trim() + String(this.$form.find('input[name="phoneNumber"]').val() || '').trim(),
            address1:  String(this.$form.find('input[name="address"]').val()    || '').trim(),
            city:      String(this.$form.find('input[name="city"]').val()       || '').trim(),
            zip:       String(this.$form.find('input[name="zipCode"]').val()        || '').trim(),
            country:   String(countryName   || '').trim(),
            linkedin:  this.$form.find('input[name="linkedinProfile"]').val()?.trim() || '',
            employer:  this.$form.find('input[name="employerCompany"]').val()?.trim() || '',
        };

        const extras = Array.from(this.selectedExtras.values());
        const rateId = this.getSelectedRateId();

        const ra = this.apartment?.rateAvailabilities?.[0]
            || this.apartment?.rateAvailabilities
            || null;

        const priceMonthly = Number(this.apartmentInfo?.pricing[0]?.amount ?? ra?.priceFrom?.gross) || 0;
        if (!rateId) throw new Error(t('rate_is_not_available', 'Rate is not available for the selected dates.'));

        const payload = {
            action: 'bl_finalize_long_booking',
            _ajax_nonce: nonce,
            apartmentId: this.apartmentId,
            unitName: this.apartmentInfo.unitName,
            unitLocation: this.apartmentInfo.addressLine1 + ', ' + this.apartmentInfo.areaName + ', ' + this.apartmentInfo.city,
            dateFrom: this.dateFrom, dateTo: this.dateTo,
            guests: this.guests,
            total: priceMonthly,
            rateId,
            extras: JSON.stringify(extras),
            customer: JSON.stringify(customer),
            token: this.checkoutToken,
            lang: (window.BL_AJAX && BL_AJAX.lang) || ''
        };

        try {
            const res = await $.ajax({ url: ajaxUrl, method: 'POST', dataType: 'json', data: payload });
            if (!res?.success) throw res;
            const redirect = res?.data?.redirect;
            if (redirect) window.location.href = `${redirect}`;
            else alert('redirect failed');
        } catch (err) {
            this.showGlobalError(t('something_went_wrong', 'Something went wrong.'));
            throw err;
        }
    }


    getSelectedRateId() {
        const ra = this.apartment?.rateAvailabilities?.[0]
            || this.apartment?.rateAvailabilities
            || null;

        const direct = Number(ra?.rateId || 0);
        if (direct) return direct;

        const firstRate = Array.isArray(ra?.rates) ? ra.rates[0] : null;
        return Number(firstRate?.id || 0);
    }


    validateForm() {
        this.setupFormValidation();
    }

    validateInputs() {
        const form = $(SELECTORS.FORM);
        const inputs = form.find(':input');
        const selects = form.find('select');
        let allValid = true;

        inputs.each(function () {
            let $containerOfInput = $(this).parent();

            if(!$containerOfInput.hasClass('required-if-showed')) {
                form.validate().element(this);
                if (!this.checkValidity() || $containerOfInput.hasClass('error')) {
                    allValid = false;
                }
            }
        });

        selects.each(function () {
            let $containerOfInput = $(this).parent();

            if(!$containerOfInput.hasClass('required-if-showed')) {
                form.validate().element(this);
                if (!this.checkValidity() || $containerOfInput.hasClass('error')) {
                    allValid = false;
                }
            }
        });

        return allValid;
    }

    scrollToElem(elem) {
        $("html, body").animate({
            scrollTop: elem.offset().top
        }, 600);
    }


    /* ---------- BOOT (Promise chain) ---------- */

    async boot() {
        // sanity
        if (!this.apartmentId || !this.dateFrom || !this.dateTo) {
            this.showError(t('booking_token_is_invalid', 'Booking token is invalid or missing required data.'));
            return;
        }

        this.showLoader();

        // 1) availability
        try {
            await this.checkAvailability();
        } catch (e) {
            this.showError(e?.message || t('apartment_is_not_available', 'The apartment is not available on these dates.'));
            return;
        }

        try {
            await this.loadFormHtml();
            await this.loadPhonesAjax();
            this.populateDialCodesFromData();
        } catch (e) {
            this.showError(e?.message || t('could_not_load_the_booking_form', 'Could not load the booking form.'));
            return;
        }

        // 2) load apartment info (extras + nice card)
        try {
            await this.fetchApartmentInformation();
        } catch {
            // not critical: we can proceed without additional fields
        }

        this.hydrateSelectedExtrasFromPayload();

        this.pruneHiddenSelectedExtras?.();

        // 3) render UI pieces
        this.renderApartmentCard();
        this.renderExtras();
        this.renderPriceBox();

        // 5) show the page
        this.showContent();

        // bind extra clicks if we need to recalculate later
        this.bindExtraClicks();
    }

    bindExtraClicks() {
        this.$extras.off('click.js-extra')
            .on('click.js-extra', '.js-extra-book', (e) => {
                const $btn = $(e.currentTarget);
                const id   = String($btn.data('id'));
                const extra = {
                    id,
                    name: String($btn.data('name') || ''),
                    price: Number($btn.data('price') || 0),
                    frequency: String($btn.data('frequency') || '')
                };

                if (this.selectedExtras.has(id)) {
                    this.selectedExtras.delete(id);          // remove selection
                } else {
                    this.selectedExtras.set(id, extra);      // select (price 0 if missing)
                }

                this.renderExtras();                       // update cards (green state)
                this.renderPriceBox();       // update price box with list of extras
            });
    }
}

/* ------------------------ INIT ------------------------ */
const longTermBookingApi = new LongTermBookingApi();
$(document).ready(() => longTermBookingApi.boot());